import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Search, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import Swal from "sweetalert2";

import { fetchConsultantsRequest } from "../../../redux/actions/consultantsActions";
import { fetchBudgetByCampagneRequest } from "../../../redux/actions/campagnesActions";
import { getAllEvaluationsRequest } from "../../../redux/actions/evaluationActions";
import "../../Formation/ResponsableFormation/RessourcesStyles/EventManagement.css";
import { buildRichEventPayload, toDatetimeLocalValue, toFloat } from "../utils/calendarHelpers";

const CONSULTANTS_PER_PAGE = 5;
const WEEKDAY_OPTIONS = [
    { value: "1", label: "Lundi", jsDay: 1 },
    { value: "2", label: "Mardi", jsDay: 2 },
    { value: "3", label: "Mercredi", jsDay: 3 },
    { value: "4", label: "Jeudi", jsDay: 4 },
    { value: "5", label: "Vendredi", jsDay: 5 },
    { value: "6", label: "Samedi", jsDay: 6 },
    { value: "0", label: "Dimanche", jsDay: 0 },
];
const RECURRENCE_FREQUENCY_OPTIONS = [
    { value: "DAILY", label: "Quotidienne" },
    { value: "WEEKLY", label: "Hebdomadaire" },
    { value: "MONTHLY", label: "Mensuelle" },
];

const EMPTY_FORM = {
    titre: "",
    description: "",
    moduleCleId: "",
    locationType: "ONSITE",
    salleId: "",
    offsiteLocation: "",
    dateDebut: "",
    dateFin: "",
    allowRegistration: false,
    registrationDeadlineHours: "",
    isPeriodic: false,
    maxPlaces: "",
    currency: "MAD",
    qrPresenceEnabled: false,
};

const EMPTY_RECURRENCE = {
    frequency: "WEEKLY",
    startDate: "",
    startTime: "09:00",
    durationHours: "1",
    sessionsCount: "",
    weekday: "1",
};

const EMPTY_BUDGET = {
    tarifFormation: 0,
    repas: 0,
    transport: 0,
    hebergement: 0,
    salleConference: 0,
    autres: 0,
    autreDescription: "",
};

const normalize = (value) => String(value || "").trim().toLowerCase();
const pad2 = (value) => String(value).padStart(2, "0");
const createTemporaryQrId = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }

    return `attendance-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};
const getConsultantSecondaryText = (consultant) =>
    consultant?.specialite
    || consultant?.poste
    || consultant?.service
    || consultant?.departement
    || consultant?.department
    || consultant?.emailContact
    || "";

const toDatetimeInputValue = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
    const timezoneOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
};

const combineDateAndTime = (dateValue, timeValue) => {
    if (!dateValue || !timeValue) return null;
    const [year, month, day] = dateValue.split("-").map(Number);
    const [hours, minutes] = timeValue.split(":").map(Number);
    return new Date(year, (month || 1) - 1, day || 1, hours || 0, minutes || 0, 0, 0);
};

const addMonthsPreservingDay = (date, monthOffset) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();

    const targetMonthIndex = month + monthOffset;
    const firstDayOfTargetMonth = new Date(year, targetMonthIndex, 1, hours, minutes, 0, 0);
    const lastDayOfTargetMonth = new Date(year, targetMonthIndex + 1, 0, hours, minutes, 0, 0);
    const safeDay = Math.min(day, lastDayOfTargetMonth.getDate());

    return new Date(
        firstDayOfTargetMonth.getFullYear(),
        firstDayOfTargetMonth.getMonth(),
        safeDay,
        hours,
        minutes,
        0,
        0
    );
};

const moveToSelectedWeekday = (date, weekdayValue) => {
    const selectedOption = WEEKDAY_OPTIONS.find((option) => option.value === String(weekdayValue));
    if (!selectedOption) return new Date(date);

    const nextDate = new Date(date);
    const delta = (selectedOption.jsDay - nextDate.getDay() + 7) % 7;
    nextDate.setDate(nextDate.getDate() + delta);
    return nextDate;
};

const inferRecurrenceFromSessions = (sessions = []) => {
    if (!Array.isArray(sessions) || !sessions.length) return EMPTY_RECURRENCE;

    const firstSession = sessions[0];
    const firstStart = firstSession?.startat ? new Date(firstSession.startat) : null;
    const firstEnd = firstSession?.endat ? new Date(firstSession.endat) : null;

    if (!(firstStart instanceof Date) || Number.isNaN(firstStart?.getTime?.())) {
        return EMPTY_RECURRENCE;
    }

    const secondStart = sessions[1]?.startat ? new Date(sessions[1].startat) : null;
    const diffInDays = secondStart && !Number.isNaN(secondStart.getTime())
        ? Math.round((secondStart.getTime() - firstStart.getTime()) / (24 * 60 * 60 * 1000))
        : null;

    let frequency = "WEEKLY";
    if (diffInDays === 1) frequency = "DAILY";
    else if (diffInDays != null && diffInDays >= 28 && diffInDays <= 31) frequency = "MONTHLY";

    const durationHours = firstEnd && !Number.isNaN(firstEnd.getTime())
        ? String(Math.max((firstEnd.getTime() - firstStart.getTime()) / (60 * 60 * 1000), 0.5))
        : "1";

    return {
        frequency,
        startDate: `${firstStart.getFullYear()}-${pad2(firstStart.getMonth() + 1)}-${pad2(firstStart.getDate())}`,
        startTime: `${pad2(firstStart.getHours())}:${pad2(firstStart.getMinutes())}`,
        durationHours,
        sessionsCount: String(sessions.length),
        weekday: String(firstStart.getDay()),
    };
};

export default function EventFormModal({
    open,
    mode,
    isEdit: isEditProp,
    event,
    modules,
    salles,
    onClose,
    onSubmit,
    loading,
    enablePresenceQr = false,
}) {
    const dispatch = useDispatch();
    const consultants = useSelector((state) => state.consultants?.consultants || []);
    const evaluations = useSelector((state) => state.evaluations?.evaluations || []);
    const campaignBudget = useSelector((state) => state.campagnes?.budgets || null);
    const isEdit = mode ? mode === "edit" : Boolean(isEditProp);

    const [tab, setTab] = useState("INFOS");
    const [form, setForm] = useState(EMPTY_FORM);
    const [recurrence, setRecurrence] = useState(EMPTY_RECURRENCE);
    const [budget, setBudget] = useState(EMPTY_BUDGET);
    const [consultSearch, setConsultSearch] = useState("");
    const [consultPage, setConsultPage] = useState(1);
    const [selectedConsultantIds, setSelectedConsultantIds] = useState(new Set());
    const [selectedEvaluationIds, setSelectedEvaluationIds] = useState(new Set());
    const [numberOfDaysHot, setNumberOfDaysHot] = useState(1);
    const [numberOfDaysCold, setNumberOfDaysCold] = useState(120);
    const [seancesCount, setSeancesCount] = useState(0);
    const [seances, setSeances] = useState([]);
    const [generatedSessionsVersion, setGeneratedSessionsVersion] = useState(0);
    const [attendanceQrCodeValue, setAttendanceQrCodeValue] = useState("");
    const [attendanceQrGenerated, setAttendanceQrGenerated] = useState(false);

    useEffect(() => {
        if (!open) return;
        dispatch(fetchConsultantsRequest());
        dispatch(getAllEvaluationsRequest());
    }, [dispatch, open]);

    useEffect(() => {
        if (!open) return;

        if (!event || !isEdit) {
            setTab("INFOS");
            setForm(EMPTY_FORM);
            setRecurrence(EMPTY_RECURRENCE);
            setBudget(EMPTY_BUDGET);
            setConsultSearch("");
            setConsultPage(1);
            setSelectedConsultantIds(new Set());
            setSelectedEvaluationIds(new Set());
            setNumberOfDaysHot(1);
            setNumberOfDaysCold(120);
            setSeancesCount(0);
            setSeances([]);
            setGeneratedSessionsVersion(0);
            setAttendanceQrCodeValue("");
            setAttendanceQrGenerated(false);
            return;
        }

        setTab("INFOS");
        setConsultSearch("");
        setConsultPage(1);
        setForm({
            titre: event.titre || "",
            description: event.description || "",
            moduleCleId: event.moduleCle?.id ? String(event.moduleCle.id) : "",
            locationType: event.locationType || "ONSITE",
            salleId: event.salle?.id ? String(event.salle.id) : "",
            offsiteLocation: event.offsiteLocation || "",
            dateDebut: toDatetimeLocalValue(event.dateDebut),
            dateFin: toDatetimeLocalValue(event.dateFin),
            allowRegistration: Boolean(event.allowRegistration),
            registrationDeadlineHours: event.registrationDeadlineHours != null ? String(event.registrationDeadlineHours) : "",
            isPeriodic: Boolean(event.isPeriodic),
            maxPlaces: Boolean(event.allowRegistration) && event.maxPlaces != null ? String(event.maxPlaces) : "",
            currency: event.currency || "MAD",
            qrPresenceEnabled: Boolean(event.qrPresenceEnabled),
        });
        setRecurrence(Boolean(event.isPeriodic) ? inferRecurrenceFromSessions(event.sessions || []) : EMPTY_RECURRENCE);
        setBudget({
            tarifFormation: event.tarifFormation || 0,
            repas: event.repas || 0,
            transport: event.transport || 0,
            hebergement: event.hebergement || 0,
            salleConference: event.salleConference || 0,
            autres: event.autres || 0,
            autreDescription: event.autreDescription || "",
        });
        setSelectedConsultantIds(new Set((event.consultants || []).map((item) => item.id)));
        setSelectedEvaluationIds(
            new Set(
                (event.evaluations || [])
                    .filter((item) => item?.typeCode === "HOT" || item?.typeCode === "COLD")
                    .map((item) => item.id)
            )
        );
        setNumberOfDaysHot(event.numberOfDaysHot != null ? event.numberOfDaysHot : 1);
        setNumberOfDaysCold(event.numberOfDaysCold != null ? event.numberOfDaysCold : 120);

        const existingSessions = (event.sessions || []).map((session, index) => ({
            id: session.id || null,
            sorder: session.sorder || index + 1,
            name: session.name || `Séance ${index + 1}`,
            dureeHours: session.dureeHours || 1,
            startat: toDatetimeLocalValue(session.startat),
            endat: toDatetimeLocalValue(session.endat),
        }));

        setSeancesCount(existingSessions.length);
        setSeances(existingSessions);
        setGeneratedSessionsVersion(Boolean(event.isPeriodic) && existingSessions.length ? 1 : 0);
        setAttendanceQrCodeValue(event.qrPresenceUrl || "");
        setAttendanceQrGenerated(Boolean(event.qrPresenceUrl || event.qrPresenceEnabled));

        if (event.moduleCle?.campagne?.id) {
            dispatch(fetchBudgetByCampagneRequest(event.moduleCle.campagne.id));
        }
    }, [dispatch, event, isEdit, open]);

    useEffect(() => {
        setConsultPage(1);
    }, [consultSearch]);

    const sallesFiltrees = useMemo(() => {
        if (!salles) return [];
        if (form.locationType === "ONSITE") return salles.filter((salle) => salle.type === "PHYSIQUE");
        if (form.locationType === "VIRTUELLE") return salles.filter((salle) => salle.type === "VIRTUELLE");
        return [];
    }, [form.locationType, salles]);

    const filteredConsultants = useMemo(() => {
        const query = normalize(consultSearch);
        return consultants.filter((item) => {
            if (!query) return true;
            const secondaryText = getConsultantSecondaryText(item);
            return normalize(item.nomContact).includes(query) || normalize(secondaryText).includes(query);
        });
    }, [consultSearch, consultants]);

    const totalConsultPages = Math.max(1, Math.ceil(filteredConsultants.length / CONSULTANTS_PER_PAGE));

    const paginatedConsultants = useMemo(() => {
        const safePage = Math.min(consultPage, totalConsultPages);
        const startIndex = (safePage - 1) * CONSULTANTS_PER_PAGE;
        return filteredConsultants.slice(startIndex, startIndex + CONSULTANTS_PER_PAGE);
    }, [consultPage, filteredConsultants, totalConsultPages]);

    useEffect(() => {
        if (consultPage > totalConsultPages) {
            setConsultPage(totalConsultPages);
        }
    }, [consultPage, totalConsultPages]);

    const filteredEvaluations = useMemo(() => {
        return (evaluations || []).filter((item) => item?.typeCode === "HOT" || item?.typeCode === "COLD");
    }, [evaluations]);

    const hotEvaluations = useMemo(
        () => filteredEvaluations.filter((item) => item.typeCode === "HOT"),
        [filteredEvaluations]
    );
    const coldEvaluations = useMemo(
        () => filteredEvaluations.filter((item) => item.typeCode === "COLD"),
        [filteredEvaluations]
    );

    const formationModules = useMemo(
        () =>
            (modules || []).filter(
                (module) =>
                    module?.type === "FORMATION"
                    || module?.typeCode === "FORMATION"
                    || module?.typeModule === "FORMATION"
                    || module?.moduleType === "FORMATION"
            ),
        [modules]
    );

    const totalCost = useMemo(
        () =>
            toFloat(budget.tarifFormation)
            + toFloat(budget.repas)
            + toFloat(budget.transport)
            + toFloat(budget.hebergement)
            + toFloat(budget.salleConference)
            + toFloat(budget.autres),
        [budget]
    );

    const remainingBudget = useMemo(() => {
        if (!campaignBudget) return 0;
        const source = campaignBudget.budgetRestant != null ? campaignBudget.budgetRestant : campaignBudget.montant;
        return toFloat(source) - totalCost;
    }, [campaignBudget, totalCost]);

    if (!open) return null;

    const setField = (name, value) => setForm((current) => ({ ...current, [name]: value }));
    const setRecurrenceField = (name, value) => setRecurrence((current) => ({ ...current, [name]: value }));

    const setAllowRegistration = (value) =>
        setForm((current) => ({
            ...current,
            allowRegistration: value,
            maxPlaces: value ? current.maxPlaces : "",
            registrationDeadlineHours: value ? current.registrationDeadlineHours : "",
        }));

    const setIsPeriodic = (value) =>
        setForm((current) => ({
            ...current,
            isPeriodic: value,
        }));

    const buildAttendanceQrValue = () => {
        const qrPayload = {
            type: "ATTENDANCE_EVENT",
            temporaryId: createTemporaryQrId(),
            title: form.titre.trim(),
            generatedAt: new Date().toISOString(),
            startAt: form.dateDebut || null,
            locationType: form.locationType || null,
            salleId: form.salleId || null,
            location: form.locationType === "OFFSITE"
                ? form.offsiteLocation || null
                : sallesFiltrees.find((item) => String(item.id) === String(form.salleId))?.nom || form.offsiteLocation || null,
        };

        return JSON.stringify(qrPayload);
    };

    const toggleConsultant = (id) =>
        setSelectedConsultantIds((current) => {
            const next = new Set(current);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const toggleEvaluation = (id) =>
        setSelectedEvaluationIds((current) => {
            const next = new Set(current);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const handleSessionsCountChange = (value) => {
        const nextValue = Math.max(0, Math.min(30, Number(value) || 0));
        setSeancesCount(nextValue);
        setSeances((current) =>
            Array.from({ length: nextValue }, (_, index) => ({
                id: current[index]?.id || null,
                sorder: current[index]?.sorder || index + 1,
                name: current[index]?.name || `Séance ${index + 1}`,
                dureeHours: current[index]?.dureeHours || 1,
                startat: current[index]?.startat || "",
                endat: current[index]?.endat || "",
            }))
        );
        setGeneratedSessionsVersion(0);
    };

    const handleModuleChange = (moduleId) => {
        setField("moduleCleId", moduleId);
        const module = (modules || []).find((item) => String(item.id) === String(moduleId));
        if (module?.campagneId) dispatch(fetchBudgetByCampagneRequest(module.campagneId));
        if (module?.campagne?.id) dispatch(fetchBudgetByCampagneRequest(module.campagne.id));
    };

    const validateRecurrence = () => {
        if (!recurrence.frequency) return "Fréquence obligatoire";
        if (!recurrence.startDate) return "Date de début obligatoire";
        if (!recurrence.startTime) return "Heure de début obligatoire";
        if (recurrence.durationHours === "" || recurrence.durationHours == null) return "Durée obligatoire";
        if (recurrence.sessionsCount === "" || recurrence.sessionsCount == null) return "Nombre de séances obligatoire";
        if (recurrence.frequency === "WEEKLY" && !recurrence.weekday && recurrence.weekday !== "0") {
            return "Jour de la semaine obligatoire";
        }
        return null;
    };

    const buildGeneratedSessions = () => {
        const startDate = combineDateAndTime(recurrence.startDate, recurrence.startTime);
        const durationHours = Number(recurrence.durationHours);
        const sessionsCountValue = Number(recurrence.sessionsCount);

        if (!startDate || Number.isNaN(startDate.getTime())) return [];
        if (!Number.isFinite(durationHours) || durationHours <= 0) return [];
        if (!Number.isInteger(sessionsCountValue) || sessionsCountValue <= 0) return [];

        const generatedSessions = [];
        let currentStart = recurrence.frequency === "WEEKLY"
            ? moveToSelectedWeekday(startDate, recurrence.weekday)
            : new Date(startDate);

        for (let index = 0; index < sessionsCountValue; index += 1) {
            if (index > 0) {
                if (recurrence.frequency === "DAILY") {
                    currentStart = new Date(currentStart.getTime() + 24 * 60 * 60 * 1000);
                } else if (recurrence.frequency === "WEEKLY") {
                    currentStart = new Date(currentStart.getTime() + 7 * 24 * 60 * 60 * 1000);
                } else {
                    currentStart = addMonthsPreservingDay(currentStart, 1);
                }
            }

            const currentEnd = new Date(currentStart.getTime() + durationHours * 60 * 60 * 1000);

            generatedSessions.push({
                id: null,
                sorder: index + 1,
                name: `Séance ${index + 1}`,
                dureeHours: String(durationHours),
                startat: toDatetimeInputValue(currentStart),
                endat: toDatetimeInputValue(currentEnd),
            });
        }

        return generatedSessions;
    };

    const handleGenerateSessions = async () => {
        const recurrenceError = validateRecurrence();
        if (recurrenceError) {
            Swal.fire({ icon: "warning", text: recurrenceError, timer: 2000, showConfirmButton: false });
            return;
        }

        if (seances.length > 0) {
            const confirmation = await Swal.fire({
                icon: "question",
                title: "Remplacer les séances existantes ?",
                text: "Les séances actuelles seront remplacées par la nouvelle génération.",
                showCancelButton: true,
                confirmButtonText: "Remplacer",
                cancelButtonText: "Annuler",
                confirmButtonColor: "#2f4595",
            });

            if (!confirmation.isConfirmed) return;
        }

        const generatedSessions = buildGeneratedSessions();
        if (!generatedSessions.length) {
            Swal.fire({ icon: "warning", text: "Impossible de générer les séances avec ces paramètres.", timer: 2200, showConfirmButton: false });
            return;
        }

        const firstSession = generatedSessions[0];
        const lastSession = generatedSessions[generatedSessions.length - 1];

        setSeances(generatedSessions);
        setSeancesCount(generatedSessions.length);
        setGeneratedSessionsVersion((current) => current + 1);
        setForm((current) => ({
            ...current,
            isPeriodic: true,
            dateDebut: firstSession.startat,
            dateFin: lastSession.endat,
        }));
        setTab("SESSIONS");
    };

    const handleGenerateAttendanceQr = () => {
        if (!form.titre.trim()) {
            Swal.fire({
                icon: "warning",
                text: "Veuillez renseigner le titre de l'événement avant de générer le QR code.",
                timer: 2200,
                showConfirmButton: false,
            });
            return;
        }

        const nextQrValue = buildAttendanceQrValue();
        setAttendanceQrCodeValue(nextQrValue);
        setAttendanceQrGenerated(true);
        setField("qrPresenceEnabled", true);
    };

    const handleRemoveAttendanceQr = () => {
        setAttendanceQrCodeValue("");
        setAttendanceQrGenerated(false);
        setField("qrPresenceEnabled", false);
    };

    const validate = () => {
        if (!form.titre.trim()) return "Titre obligatoire";
        if (!form.moduleCleId) return "Module obligatoire";
        if (!form.dateDebut || !form.dateFin) return "Dates obligatoires";
        if (form.locationType === "OFFSITE" && !form.offsiteLocation.trim()) return "Lieu externe obligatoire";
        if (form.locationType !== "OFFSITE" && !form.salleId) return "Salle obligatoire";
        if (form.allowRegistration) {
            if (form.maxPlaces === "" || form.maxPlaces == null) return "Limite de places obligatoire";
            if (form.registrationDeadlineHours === "" || form.registrationDeadlineHours == null) {
                return "Délai d'inscription obligatoire";
            }
        }
        if (form.isPeriodic) {
            const recurrenceError = validateRecurrence();
            if (recurrenceError) return recurrenceError;
        }
        if (!seances.length) return "Au moins une séance est obligatoire";

        const validEvalIds = new Set(
            (evaluations || [])
                .filter((item) => item?.typeCode === "HOT" || item?.typeCode === "COLD")
                .map((item) => item.id)
        );

        for (const selectedId of selectedEvaluationIds) {
            if (!validEvalIds.has(selectedId)) {
                return "Seules les évaluations de type HOT ou COLD peuvent être associées à un événement.";
            }
        }

        return null;
    };

    const handleSubmit = () => {
        const error = validate();
        if (error) {
            Swal.fire({ icon: "warning", text: error, timer: 2200, showConfirmButton: false });
            return;
        }

        onSubmit(
            buildRichEventPayload({
                event: isEdit ? event : null,
                form,
                budget,
                consultants: Array.from(selectedConsultantIds),
                evaluations: Array.from(selectedEvaluationIds),
                sessions: seances,
                numberOfDaysHot,
                numberOfDaysCold,
                budgetRestant: remainingBudget,
                qrPresenceEnabled: enablePresenceQr ? attendanceQrGenerated : undefined,
            })
        );
    };

    return (
        <div className="ev-modal-overlay" onClick={onClose}>
            <div className="ev-modal ev-modal--large" onClick={(evt) => evt.stopPropagation()}>
                <div className="ev-modal-header">
                    <h3>{isEdit ? "Modifier l'événement" : "Ajouter un événement"}</h3>
                    <button type="button" className="ev-x" onClick={onClose} aria-label="Fermer">
                        <X size={18} />
                    </button>
                </div>

                <div className="ev-modal-tabs">
                    {["INFOS", "SESSIONS", "CONSULTANTS", "EVALUATIONS", "BUDGET"].map((item) => (
                        <button
                            key={item}
                            type="button"
                            className={`ev-modal-tab ${tab === item ? "active" : ""}`}
                            onClick={() => setTab(item)}
                        >
                            {item === "SESSIONS"
                                ? `Séances (${seancesCount})`
                                : item === "CONSULTANTS"
                                    ? `Consultants (${selectedConsultantIds.size})`
                                    : item === "EVALUATIONS"
                                        ? `Évaluations (${selectedEvaluationIds.size})`
                                        : item.charAt(0) + item.slice(1).toLowerCase()}
                        </button>
                    ))}
                </div>

                <div className="ev-modal-body">
                    {tab === "INFOS" && (
                        <div className="ev-grid">
                            <div className="ev-field">
                                <label>Titre *</label>
                                <input className="ev-input" value={form.titre} onChange={(evt) => setField("titre", evt.target.value)} />
                            </div>
                            <div className="ev-field">
                                <label>Module *</label>
                                <select className="ev-input" value={form.moduleCleId} onChange={(evt) => handleModuleChange(evt.target.value)}>
                                    <option value="">— Choisir —</option>
                                    {formationModules.map((module) => (
                                        <option key={module.id} value={String(module.id)}>
                                            {module.nom}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="ev-field">
                                <label>Lieu *</label>
                                <select
                                    className="ev-input"
                                    value={form.locationType}
                                    onChange={(evt) =>
                                        setForm((current) => ({
                                            ...current,
                                            locationType: evt.target.value,
                                            salleId: "",
                                            offsiteLocation: "",
                                        }))
                                    }
                                >
                                    <option value="ONSITE">On-site</option>
                                    <option value="OFFSITE">Off-site</option>
                                    <option value="VIRTUELLE">Virtuelle</option>
                                </select>
                            </div>
                            {form.locationType === "OFFSITE" ? (
                                <div className="ev-field ev-span-2">
                                    <label>Adresse exacte</label>
                                    <input className="ev-input" value={form.offsiteLocation} onChange={(evt) => setField("offsiteLocation", evt.target.value)} />
                                </div>
                            ) : (
                                <>
                                    <div className="ev-field">
                                        <label>{form.locationType === "VIRTUELLE" ? "Salle virtuelle" : "Salle"}</label>
                                        <select className="ev-input" value={form.salleId} onChange={(evt) => setField("salleId", evt.target.value)}>
                                            <option value="">— Choisir —</option>
                                            {sallesFiltrees.map((salle) => (
                                                <option key={salle.id} value={String(salle.id)}>
                                                    {salle.nom}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {form.locationType === "VIRTUELLE" && (
                                        <div className="ev-field">
                                            <label>Lien visio</label>
                                            <input className="ev-input" value={form.offsiteLocation} onChange={(evt) => setField("offsiteLocation", evt.target.value)} />
                                        </div>
                                    )}
                                </>
                            )}
                            <div className="ev-field">
                                <label>Début *</label>
                                <input type="datetime-local" className="ev-input" value={form.dateDebut} onChange={(evt) => setField("dateDebut", evt.target.value)} />
                            </div>
                            <div className="ev-field">
                                <label>Fin *</label>
                                <input type="datetime-local" className="ev-input" value={form.dateFin} onChange={(evt) => setField("dateFin", evt.target.value)} />
                            </div>
                            <div className="ev-field ev-span-2">
                                <label>Description</label>
                                <textarea className="ev-input" rows={3} value={form.description} onChange={(evt) => setField("description", evt.target.value)} />
                            </div>
                            <div className="ev-field ev-span-2">
                                <label>Autoriser l'inscription</label>
                                <div className="ev-radio-group" role="radiogroup" aria-label="Autoriser l'inscription">
                                    <label className="ev-radio-option">
                                        <input type="radio" name="allowRegistration" checked={form.allowRegistration === true} onChange={() => setAllowRegistration(true)} />
                                        <span>Oui</span>
                                    </label>
                                    <label className="ev-radio-option">
                                        <input type="radio" name="allowRegistration" checked={form.allowRegistration === false} onChange={() => setAllowRegistration(false)} />
                                        <span>Non</span>
                                    </label>
                                </div>
                            </div>
                            {form.allowRegistration && (
                                <>
                                    <div className="ev-field">
                                        <label>Limite de places</label>
                                        <input type="number" className="ev-input" value={form.maxPlaces} onChange={(evt) => setField("maxPlaces", evt.target.value)} />
                                    </div>
                                    <div className="ev-field">
                                        <label>Arrêter l'inscription  avant (heures)</label>
                                        <input
                                            type="number"
                                            className="ev-input"
                                            value={form.registrationDeadlineHours}
                                            onChange={(evt) => setField("registrationDeadlineHours", evt.target.value)}
                                        />
                                    </div>
                                </>
                            )}
                            <div className="ev-field ev-span-2">
                                <label>Événement périodique </label>
                                <div className="ev-radio-group" role="radiogroup" aria-label="Événement périodique">
                                    <label className="ev-radio-option">
                                        <input type="radio" name="isPeriodic" checked={form.isPeriodic === false} onChange={() => setIsPeriodic(false)} />
                                        <span>Non</span>
                                    </label>
                                    <label className="ev-radio-option">
                                        <input type="radio" name="isPeriodic" checked={form.isPeriodic === true} onChange={() => setIsPeriodic(true)} />
                                        <span>Oui</span>
                                    </label>
                                </div>
                            </div>
                            {form.isPeriodic && (
                                <div className="ev-box ev-span-2">
                                    <p className="ev-section-title">Paramètres de répétition</p>
                                    <div className="ev-grid">
                                        <div className="ev-field">
                                            <label>Fréquence *</label>
                                            <select className="ev-input" value={recurrence.frequency} onChange={(evt) => setRecurrenceField("frequency", evt.target.value)}>
                                                {RECURRENCE_FREQUENCY_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="ev-field">
                                            <label>Date de début *</label>
                                            <input type="date" className="ev-input" value={recurrence.startDate} onChange={(evt) => setRecurrenceField("startDate", evt.target.value)} />
                                        </div>
                                        <div className="ev-field">
                                            <label>Heure de début *</label>
                                            <input type="time" className="ev-input" value={recurrence.startTime} onChange={(evt) => setRecurrenceField("startTime", evt.target.value)} />
                                        </div>
                                        <div className="ev-field">
                                            <label>Durée d'une séance (heures) *</label>
                                            <input type="number" step={0.5} min={0.5} className="ev-input" value={recurrence.durationHours} onChange={(evt) => setRecurrenceField("durationHours", evt.target.value)} />
                                        </div>
                                        <div className="ev-field">
                                            <label>Nombre de séances *</label>
                                            <input type="number" min={1} className="ev-input" value={recurrence.sessionsCount} onChange={(evt) => setRecurrenceField("sessionsCount", evt.target.value)} />
                                        </div>
                                        {recurrence.frequency === "WEEKLY" && (
                                            <div className="ev-field">
                                                <label>Jour de la semaine *</label>
                                                <select className="ev-input" value={recurrence.weekday} onChange={(evt) => setRecurrenceField("weekday", evt.target.value)}>
                                                    {WEEKDAY_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>{option.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                    <div className="ev-recurrence-actions">
                                        <button type="button" className="ev-btn ev-btn-primary" onClick={handleGenerateSessions}>
                                            Générer les séances
                                        </button>
                                        <span className="ev-inline-hint">
                                            Les séances générées seront ensuite visibles et modifiables dans l'onglet Séances.
                                        </span>
                                    </div>
                                </div>
                            )}
                            {enablePresenceQr && (
                                <div className="ev-qr-actions ev-span-2">
                                    <div className="ev-qr-actions__header">
                                        <div className="ev-qr-panel__title">QR code de présence</div>
                                        <p className="ev-qr-panel__text">
                                            Générez un QR code immédiatement pour prévisualiser le pointage de présence dans le modal.
                                        </p>
                                    </div>
                                    {!attendanceQrGenerated ? (
                                        <button type="button" className="ev-btn ev-btn-primary" onClick={handleGenerateAttendanceQr}>
                                            Générer le QR code de présence
                                        </button>
                                    ) : (
                                        <div className="ev-qr-actions__buttons">
                                            <button type="button" className="ev-btn ev-btn-primary" onClick={handleGenerateAttendanceQr}>
                                                Régénérer le QR code
                                            </button>
                                            <button type="button" className="ev-btn ev-btn-light" onClick={handleRemoveAttendanceQr}>
                                                Supprimer le QR code
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {enablePresenceQr && attendanceQrGenerated && attendanceQrCodeValue && (
                                <div className="ev-qr-panel ev-span-2">
                                    <div className="ev-qr-panel__content">
                                        <div>
                                            <div className="ev-qr-panel__title">QR code de présence prêt</div>
                                            <p className="ev-qr-panel__text">
                                                {event?.id && event?.qrPresenceUrl && attendanceQrCodeValue === event.qrPresenceUrl
                                                    ? "QR code actuel enregistré pour cet événement."
                                                    : "QR code temporaire généré côté interface. Il sera conservé jusqu'à l'enregistrement."}
                                            </p>
                                        </div>
                                        <div className="ev-qr-panel__code" aria-label="QR code de présence">
                                            <QRCodeSVG value={attendanceQrCodeValue} size={132} />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "SESSIONS" && (
                        <div className="ev-box">
                            <p className="ev-section-title">Nombre de séances</p>
                            <input
                                type="number"
                                min={0}
                                max={30}
                                className="ev-input"
                                style={{ width: 100, marginBottom: 10 }}
                                value={seancesCount}
                                disabled={form.isPeriodic}
                                onChange={(evt) => handleSessionsCountChange(evt.target.value)}
                            />
                            {form.isPeriodic && (
                                <p className="ev-inline-hint" style={{ marginBottom: 16 }}>
                                    Les séances sont générées depuis les paramètres de répétition. Vous pouvez toutefois ajuster manuellement les lignes ci-dessous.
                                </p>
                            )}
                            <div className="ev-table-wrap">
                                <table className="ev-table ev-table--sm">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Nom</th>
                                            <th>Durée (h)</th>
                                            <th>Début</th>
                                            <th>Fin</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {seances.map((session, index) => (
                                            <tr key={`${session.id || "new"}-${generatedSessionsVersion}-${index}`}>
                                                <td>{session.sorder}</td>
                                                <td>
                                                    <input
                                                        className="ev-input ev-input--sm"
                                                        value={session.name}
                                                        onChange={(evt) =>
                                                            setSeances((current) =>
                                                                current.map((item, itemIndex) =>
                                                                    itemIndex === index ? { ...item, name: evt.target.value } : item
                                                                )
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        step={0.5}
                                                        className="ev-input ev-input--sm"
                                                        value={session.dureeHours}
                                                        onChange={(evt) =>
                                                            setSeances((current) =>
                                                                current.map((item, itemIndex) =>
                                                                    itemIndex === index ? { ...item, dureeHours: evt.target.value } : item
                                                                )
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="datetime-local"
                                                        className="ev-input ev-input--sm"
                                                        value={session.startat}
                                                        onChange={(evt) =>
                                                            setSeances((current) =>
                                                                current.map((item, itemIndex) =>
                                                                    itemIndex === index ? { ...item, startat: evt.target.value } : item
                                                                )
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="datetime-local"
                                                        className="ev-input ev-input--sm"
                                                        value={session.endat}
                                                        onChange={(evt) =>
                                                            setSeances((current) =>
                                                                current.map((item, itemIndex) =>
                                                                    itemIndex === index ? { ...item, endat: evt.target.value } : item
                                                                )
                                                            )
                                                        }
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                        {!seances.length && (
                                            <tr>
                                                <td colSpan={5} style={{ textAlign: "center", color: "#6b7280" }}>
                                                    Aucune séance définie.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {tab === "CONSULTANTS" && (
                        <div className="ev-box">
                            <div className="ev-search-wrap" style={{ marginBottom: 16 }}>
                                <Search size={14} className="ev-search-icon" />
                                <input
                                    className="ev-search"
                                    placeholder="Rechercher consultant..."
                                    value={consultSearch}
                                    onChange={(evt) => setConsultSearch(evt.target.value)}
                                />
                            </div>
                            <div className="ev-consultant-list">
                                {paginatedConsultants.map((item) => {
                                    const helperText = getConsultantSecondaryText(item);
                                    const isSelected = selectedConsultantIds.has(item.id);

                                    return (
                                        <label key={item.id} className={`ev-consultant-row ${isSelected ? "is-selected" : ""}`}>
                                            <input
                                                type="checkbox"
                                                className="ev-consultant-checkbox"
                                                checked={isSelected}
                                                onChange={() => toggleConsultant(item.id)}
                                            />
                                            <div className="ev-consultant-main">
                                                <div className="ev-consultant-name">{item.nomContact}</div>
                                            </div>
                                            {helperText ? <div className="ev-consultant-meta">{helperText}</div> : <div />}
                                        </label>
                                    );
                                })}
                                {!paginatedConsultants.length && (
                                    <div className="ev-consultant-empty">Aucun consultant trouvé.</div>
                                )}
                            </div>
                            <div className="ev-consultant-pagination">
                                <button
                                    type="button"
                                    className="ev-btn ev-btn-light"
                                    onClick={() => setConsultPage((current) => Math.max(1, current - 1))}
                                    disabled={consultPage <= 1}
                                >
                                    Précédent
                                </button>
                                <span className="ev-consultant-page-label">Page {consultPage} / {totalConsultPages}</span>
                                <button
                                    type="button"
                                    className="ev-btn ev-btn-light"
                                    onClick={() => setConsultPage((current) => Math.min(totalConsultPages, current + 1))}
                                    disabled={consultPage >= totalConsultPages}
                                >
                                    Suivant
                                </button>
                            </div>
                        </div>
                    )}

                    {tab === "EVALUATIONS" && (
                        <div className="ev-box">
                            <div className="ev-grid ev-grid--cols-3" style={{ marginBottom: 16 }}>
                                <div className="ev-field">
                                    <label>Délai à chaud (J+)</label>
                                    <input
                                        type="number"
                                        className="ev-input"
                                        value={numberOfDaysHot}
                                        onChange={(evt) => setNumberOfDaysHot(Math.max(1, Number(evt.target.value) || 1))}
                                    />
                                </div>
                                <div className="ev-field">
                                    <label>Délai à froid (J+)</label>
                                    <input
                                        type="number"
                                        className="ev-input"
                                        value={numberOfDaysCold}
                                        onChange={(evt) => setNumberOfDaysCold(Math.max(1, Number(evt.target.value) || 1))}
                                    />
                                </div>
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                                <div>
                                    <h4 style={{ fontSize: "14px", fontWeight: "600", color: "#2f4595", marginBottom: 12, paddingBottom: 4, borderBottom: "1px solid #e5e7eb" }}>
                                        Évaluations à chaud
                                    </h4>
                                    {hotEvaluations.length === 0 ? (
                                        <p style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic", margin: "4px 0" }}>
                                            Aucune évaluation à chaud
                                        </p>
                                    ) : (
                                        <div className="ev-grid ev-grid--cols-2">
                                            {hotEvaluations.map((item) => (
                                                <div key={item.id} className="ev-import-eval-card">
                                                    <label className="ev-toggle">
                                                        <input type="checkbox" checked={selectedEvaluationIds.has(item.id)} onChange={() => toggleEvaluation(item.id)} />
                                                        <span className="ev-slider" />
                                                    </label>
                                                    <div className="ev-import-eval-info">
                                                        <span style={{ fontWeight: 550 }}>{item.name || item.nom}</span>
                                                        <span className="ev-badge ev-badge--soft-blue" style={{ fontSize: 9 }}>À chaud</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <h4 style={{ fontSize: "14px", fontWeight: "600", color: "#2f4595", marginBottom: 12, paddingBottom: 4, borderBottom: "1px solid #e5e7eb" }}>
                                        Évaluations à froid
                                    </h4>
                                    {coldEvaluations.length === 0 ? (
                                        <p style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic", margin: "4px 0" }}>
                                            Aucune évaluation à froid
                                        </p>
                                    ) : (
                                        <div className="ev-grid ev-grid--cols-2">
                                            {coldEvaluations.map((item) => (
                                                <div key={item.id} className="ev-import-eval-card">
                                                    <label className="ev-toggle">
                                                        <input type="checkbox" checked={selectedEvaluationIds.has(item.id)} onChange={() => toggleEvaluation(item.id)} />
                                                        <span className="ev-slider" />
                                                    </label>
                                                    <div className="ev-import-eval-info">
                                                        <span style={{ fontWeight: 550 }}>{item.name || item.nom}</span>
                                                        <span className="ev-badge ev-badge--soft-blue" style={{ fontSize: 9 }}>À froid</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {tab === "BUDGET" && (
                        <div className="ev-box">
                            <div className="ev-grid">
                                <div className="ev-field">
                                    <label>Devise</label>
                                    <input className="ev-input" value={form.currency} onChange={(evt) => setField("currency", evt.target.value)} />
                                </div>

                                <div className="ev-field">
                                    <label>Tarif formation</label>
                                    <input type="number" className="ev-input" value={budget.tarifFormation} onChange={(evt) => setBudget((current) => ({ ...current, tarifFormation: evt.target.value }))} />
                                </div>
                                <div className="ev-field">
                                    <label>Hébergement</label>
                                    <input type="number" className="ev-input" value={budget.hebergement} onChange={(evt) => setBudget((current) => ({ ...current, hebergement: evt.target.value }))} />
                                </div>
                                <div className="ev-field">
                                    <label>Transport</label>
                                    <input type="number" className="ev-input" value={budget.transport} onChange={(evt) => setBudget((current) => ({ ...current, transport: evt.target.value }))} />
                                </div>
                                <div className="ev-field">
                                    <label>Salle conférence</label>
                                    <input type="number" className="ev-input" value={budget.salleConference} onChange={(evt) => setBudget((current) => ({ ...current, salleConference: evt.target.value }))} />
                                </div>
                                <div className="ev-field">
                                    <label>Repas</label>
                                    <input type="number" className="ev-input" value={budget.repas} onChange={(evt) => setBudget((current) => ({ ...current, repas: evt.target.value }))} />
                                </div>
                                <div className="ev-field">
                                    <label>Autres charges</label>
                                    <input type="number" className="ev-input" value={budget.autres} onChange={(evt) => setBudget((current) => ({ ...current, autres: evt.target.value }))} />
                                </div>
                                <div className="ev-field ev-span-2">
                                    <label>Détail autres charges</label>
                                    <textarea className="ev-input" rows={3} value={budget.autreDescription} onChange={(evt) => setBudget((current) => ({ ...current, autreDescription: evt.target.value }))} />
                                </div>
                            </div>
                            <div style={{ marginTop: 24, padding: 16, background: "#f8f9fa", borderRadius: 8 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                                    <span>Total saisi :</span>
                                    <b style={{ color: "#2f4595" }}>{totalCost.toFixed(2)} {form.currency}</b>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                                    <span>Budget campagne :</span>
                                    <b style={{ color: "#2f4595" }}>
                                        {campaignBudget?.montant != null ? `${toFloat(campaignBudget.montant).toFixed(2)} MAD` : "—"}
                                    </b>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <span>Restant campagne :</span>
                                    <b style={{ color: remainingBudget < 0 ? "#dc3545" : "#059669" }}>
                                        {campaignBudget ? `${remainingBudget.toFixed(2)} MAD` : "—"}
                                    </b>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="ev-modal-footer">
                    <button type="button" className="ev-btn ev-btn-light" onClick={onClose}>Annuler</button>
                    <button type="button" className="ev-btn ev-btn-primary" onClick={handleSubmit} disabled={loading}>
                        {loading ? "Enregistrement..." : isEdit ? "Enregistrer" : "Créer"}
                    </button>
                </div>
            </div>
        </div>
    );
}
